import { Link } from "@tanstack/react-router";
import { AlertTriangle, Bell, CheckCircle, ChevronRight, Info } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { Notification } from "@/hooks/use-notifications";
import { useMarkAllNotificationsRead, useNotifications } from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

function relativeTime(dateStr: string | null): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function NotificationIcon({ type }: { type: string | null }) {
  if (type === "success") return <CheckCircle className="h-4 w-4 shrink-0 text-emerald-500" />;
  if (type === "warning") return <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />;
  if (type === "info") return <Info className="h-4 w-4 shrink-0 text-blue-500" />;
  return <Bell className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function NotificationItem({ n }: { n: Notification }) {
  return (
    <div
      className={cn(
        "group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/50",
        !n.read && "bg-primary/[0.04]",
      )}
    >
      <div className="mt-0.5 flex shrink-0 items-center gap-1.5">
        {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
        {n.read && <span className="h-1.5 w-1.5" />}
        <NotificationIcon type={n.type} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              "text-[13px] leading-snug",
              n.read ? "font-normal text-foreground" : "font-semibold text-foreground",
            )}
          >
            {n.title ?? "Notification"}
          </p>
          <span className="shrink-0 text-[11px] text-muted-foreground">{relativeTime(n.date)}</span>
        </div>
        {n.message && (
          <p className="mt-0.5 line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
            {n.message}
          </p>
        )}
      </div>
    </div>
  );
}

interface NotificationsPopoverProps {
  unreadCount: number;
}

export function NotificationsPopover({ unreadCount }: NotificationsPopoverProps) {
  const [open, setOpen] = useState(false);
  const { data: notifications, isLoading } = useNotifications();
  const markAllRead = useMarkAllNotificationsRead();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative rounded-md p-1.5 text-white/85 transition-colors hover:bg-white/10"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span
              className="absolute right-0.5 top-0.5 flex h-[14px] min-w-[14px] items-center justify-center rounded-full px-1 font-mono text-[9px] font-semibold text-white"
              style={{ background: "var(--primary)" }}
            >
              {unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-2">
            <h3 className="text-[13px] font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <span className="rounded-full bg-primary/10 px-1.5 py-px text-[11px] font-semibold text-primary">
                {unreadCount}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[12px] text-muted-foreground hover:text-foreground"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all read
          </Button>
        </div>

        {/* Body */}
        <div className="max-h-[420px] overflow-y-auto">
          {isLoading && (
            <div className="space-y-px py-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-3">
                  <div className="mt-1 h-4 w-4 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-full animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && (!notifications || notifications.length === 0) && (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              <Bell className="h-7 w-7 text-muted-foreground/40" />
              <p className="text-[13px] text-muted-foreground">You're all caught up</p>
            </div>
          )}

          {!isLoading && notifications && notifications.length > 0 && (
            <div className="divide-y divide-border/60">
              {notifications.map((n) => (
                <NotificationItem key={n.id} n={n} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-2.5">
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="flex items-center gap-1 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
          >
            View all notifications
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
